import { installDomTranslationGuard } from './libs/domTranslationGuard';
import React, { Suspense } from 'react'
import { createRoot } from 'react-dom/client';
import './index.css'
import App from './App'
import { firebaseConfig, setupHelpers } from "./libs/firebase";
import { initAuth } from "./libs/initAuth";
import { isStaging, shouldReportToSentry } from "./libs/environments";
import { firebaseHelper } from 'ui';

installDomTranslationGuard();
firebaseHelper.initializeApp(firebaseConfig);
initAuth(firebaseHelper.firebaseApp);
setupHelpers();

// Same hostname rule as Firebase: only mattoboard.com and beta.mattoboard.com are
// production, so daily.mattoboard.com and *.web.app previews report to Sentry as staging.
const useStaging = isStaging();

// The hosted SketchUp panel lives at /sketchup/* and renders its own app
// shell (no Userpilot, no GPU detection, no PastDueBanner, no
// ImpersonationBanner, no board-chunk prewarm). Firebase init above and
// Sentry init below stay shared. Lazy so the panel is its own chunk and the
// website entry doesn't grow.
const isSketchupPanel = window.location.pathname.startsWith('/sketchup');

// Lazy load Sentry
const initializeSentry = async () => {
  try {
    const Sentry = await import("@sentry/react");

    Sentry.init({
      dsn: "https://5c18a30196a940c9ade7515923c03671@o1220868.ingest.sentry.io/6367335",
      tracesSampleRate: 0.1,
      autoSessionTracking: false,
      enabled: shouldReportToSentry(),
      environment: useStaging ? 'staging' : 'production',
      denyUrls: [/js\.userpilot\.io/], // Userpilot SDK's own errors (MATTOBOARD-7FH)
    });
    if (isSketchupPanel) {
      Sentry.setTag('surface', 'sketchup');
    }
  } catch (error) {
    console.warn('Failed to initialize Sentry:', error);
  }
};

const SketchupApp = React.lazy(() => import('./sketchup/SketchupApp'));

const container = document.getElementById('root');
const root = createRoot(container);
// newer version uses React18
root.render(
  <React.StrictMode>
    {isSketchupPanel ? (
      <Suspense fallback={null}>
        <SketchupApp />
      </Suspense>
    ) : (
      <App />
    )}
  </React.StrictMode>
)

// Initialize Sentry after the app has rendered
initializeSentry();
